<script setup lang="ts">
import { computed, ref } from "vue";
import { formatDecimal } from "@yawhub/core";
import { calendarDays, type CalendarDay } from "../activityCalendar";
const props = defineProps<{ result: any }>();
const inspectedKey = ref<string>();
const metric = computed(() => props.result.query.metrics[0]);
const timezone = computed(
  () => props.result.metadata.period?.timezone ?? "Europe/Copenhagen",
);
const today = computed(() =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: props.result.metadata.period?.timezone ?? "Europe/Copenhagen",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date()),
);
const year = computed(
  () => props.result.display?.calendarYear ?? Number(today.value.slice(0, 4)),
);
const weeks = computed(() =>
  calendarDays(
    year.value,
    props.result.rows,
    metric.value.alias,
    metric.value.op.startsWith("count"),
    today.value,
  ),
);
const inspected = computed(() =>
  weeks.value
    .flat()
    .find((day) => day.key === inspectedKey.value && day.inside),
);
const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Maj",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dec",
];
const label = (day: CalendarDay) =>
  `${day.key}: ${day.future ? "Fremtidig dato" : day.value === null ? "Ingen måling" : formatDecimal(day.value)}`;
function navigate(event: KeyboardEvent) {
  const offset = (
    { ArrowRight: 7, ArrowLeft: -7, ArrowDown: 1, ArrowUp: -1 } as Record<
      string,
      number
    >
  )[event.key];
  if (offset === undefined) return;
  event.preventDefault();
  const cells = Array.from(
    (event.currentTarget as HTMLElement)
      .closest(".calendar-grid")!
      .querySelectorAll<HTMLButtonElement>("button"),
  );
  const i = cells.indexOf(event.target as HTMLButtonElement);
  cells[Math.max(0, Math.min(cells.length - 1, i + offset))]?.focus();
}
</script>
<template>
  <figure class="activity-calendar">
    <div
      class="calendar-scroll"
      role="region"
      :aria-label="'Aktivitetskalender ' + year"
      tabindex="0"
    >
      <div class="calendar-grid">
        <div class="calendar-days" aria-hidden="true">
          <span></span><span>Man</span><span></span><span>Ons</span><span></span
          ><span>Fre</span><span></span><span>Søn</span>
        </div>
        <div v-for="(week, i) in weeks" :key="i" class="calendar-week">
          <span class="calendar-month">{{
            week.some((d) => d.inside && d.day === 1)
              ? months[week.find((d) => d.inside && d.day === 1)!.month]
              : ""
          }}</span>
          <template v-for="day in week" :key="day.key">
            <button
              v-if="day.inside"
              type="button"
              class="calendar-cell"
              :class="[
                'level-' + day.level,
                { future: day.future, selected: inspected?.key === day.key },
              ]"
              :aria-label="label(day)"
              :title="label(day)"
              :tabindex="
                day.key === (inspected?.key ?? `${year}-01-01`) ? 0 : -1
              "
              @focus="inspectedKey = day.key"
              @pointerenter="inspectedKey = day.key"
              @click="inspectedKey = day.key"
              @keydown="navigate"
            />
            <span v-else class="calendar-spacer" />
          </template>
        </div>
      </div>
    </div>
    <figcaption>
      <strong>{{
        inspected ? label(inspected) : "Vælg en dag for at se værdien"
      }}</strong
      ><span>{{ year }} · {{ timezone }}</span>
    </figcaption>
    <div class="calendar-legend">
      <span class="calendar-cell level-0"></span><span>Ingen måling</span
      ><span class="calendar-cell level-1"></span><span>0</span
      ><span class="calendar-cell level-2"></span
      ><span class="calendar-cell level-3"></span
      ><span class="calendar-cell level-4"></span
      ><span class="calendar-cell level-5"></span><span>Større værdi</span>
    </div>
    <p class="muted small">
      Farvestyrken viser værdiens størrelse. Fortegn og præcise værdier vises
      for hver dag.
    </p>
  </figure>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/ActivityCalendar.scss"
></style>
